import { useAvailableTenants } from '@/api/hooks/useAvailableTenants'
import { MRDCell } from '@/components/Grid/CellRenderers'
import { useMappingContext } from '@/contexts/MappingContext'
import { useTenantStore } from '@/stores/tenantStore'
import {
  CrossroadsEntity,
  CrossroadsEntityType,
  CrossroadsMapping,
  CrossroadsMappingType,
  ENTITY_GRID_ENDPOINT_MAP,
} from '@api/types'
import { GraviGrid, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useChildMappingGridParams } from '@pages/Dashboard/panels/ReferenceDataPanel/hooks/useChildMappingGridParams'
import { useDebouncedSearch } from '@pages/Dashboard/panels/ReferenceDataPanel/hooks/useDebouncedSearch'
import { useLinkedMappings } from '@pages/Dashboard/panels/ReferenceDataPanel/hooks/useLinkedMappings'
import { usePaginatedGridDatasource } from '@pages/Dashboard/panels/ReferenceDataPanel/hooks/usePaginatedGridDatasource'
import {
  ColDef,
  GetRowIdParams,
  GroupCellRenderer,
  ICellRendererParams,
  RowClickedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community'
import { Input, Select, Switch } from 'antd'
import React, { useCallback, useMemo, useState } from 'react'
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels'
import { useSearchParams } from 'react-router-dom'

interface MRDGridProps {
  filterType: CrossroadsEntityType
}

export function CrossroadsEntityGrid(props: MRDGridProps) {
  const [params] = useSearchParams()
  const prefilteredGoIds = useMemo(
    () => params.get('go_ids')?.split(',') ?? [],
    [params],
  )

  const [filterToSelectedMapping, setFilterToSelectedMapping] = useState(false)
  const [selectedTenantGoid, setSelectedTenantGoid] = useState<
    string | undefined
  >(undefined)
  const { availableTenants } = useAvailableTenants()
  const { selectedTenant } = useTenantStore()
  const { selectedMapping, setDraggedEntity } = useMappingContext()
  const [selectedEntity, setSelectedEntity] = useState<CrossroadsEntity | null>(
    null,
  )
  const { searchTerm, setSearchTerm } = useDebouncedSearch()

  const endpoint: string = useMemo(() => {
    const isFilteringToMapping = filterToSelectedMapping && selectedMapping
    const isFilteringWithoutEntityType =
      !props.filterType && prefilteredGoIds.length > 0

    if (isFilteringToMapping || isFilteringWithoutEntityType)
      return '/crossroads/entity/object/grid'
    return ENTITY_GRID_ENDPOINT_MAP[props.filterType]
  }, [filterToSelectedMapping, selectedMapping, props.filterType])

  const resolvedSearchTerm: string | undefined = useMemo(() => {
    // Priority: URL go_ids > selected mapping goids > search term
    if (prefilteredGoIds.length > 0) {
      return prefilteredGoIds.join(',')
    }
    if (
      filterToSelectedMapping &&
      selectedMapping &&
      selectedMapping.goids.length !== 0
    ) {
      return selectedMapping.goids.join(',')
    }
    if (!filterToSelectedMapping && searchTerm) {
      return searchTerm
    }
    return undefined
  }, [prefilteredGoIds, selectedMapping, filterToSelectedMapping, searchTerm])

  const { datasource, columnDefs, totalRows } =
    usePaginatedGridDatasource<CrossroadsEntity>({
      endpoint,
      searchTerm: resolvedSearchTerm,
      recordOwner: selectedTenantGoid,
    })

  const { data: linkedEntities, isLoading: linkedEntitiesLoading } =
    useLinkedMappings(selectedEntity?.goid)

  const colDefs = useMemo<ColDef<CrossroadsEntity>[]>(() => {
    const filteredDefs = columnDefs.filter(
      (def) =>
        def.field !== 'name' &&
        def.field !== 'goid' &&
        def.field !== 'record_owner',
    )
    const autoDefs = filteredDefs.map((def) => {
      return {
        headerName: def.headerName,
        field: def.field,
        type: def.type,
        hide: true,
      }
    })
    return [
      {
        headerName: 'Name',
        field: 'name',
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params
          return (
            <div
              draggable='true'
              onDragStart={() => setDraggedEntity(data)}
              onDragEnd={() => setDraggedEntity(null)}
            >
              <Texto>{data.name}</Texto>
            </div>
          )
        },
      },
      { headerName: 'GOID', field: 'goid' },
      {
        headerName: 'Record Owner',
        field: 'record_owner',
        cellRenderer: (params: ICellRendererParams<CrossroadsEntity>) => {
          const owner = params.data?.record_owner
          const goids = owner ? [owner] : null
          return <MRDCell goids={goids} objectType='company' />
        },
      },
      ...autoDefs,
    ]
  }, [columnDefs])

  const linkedMappingGridColDefs: ColDef<CrossroadsMapping, unknown>[] = [
    {
      headerName: 'Tenant',
      field: 'tenant',
      cellRenderer: GroupCellRenderer,
    },
    { headerName: 'Name', field: 'display_name' },
    { headerName: 'System', field: 'source_system' },
    { headerName: 'Type', field: 'type' },
    {
      headerName: 'Mapped To',
      field: 'goids',
      cellRenderer: (params) => (
        <MRDCell
          goids={params.data!.goids}
          objectType={params.data!.type}
          maxVisible={1}
        />
      ),
    },
  ]

  const detailGridRendererParams = useChildMappingGridParams(false)

  // Callbacks

  const getRowId = useCallback((params: GetRowIdParams<CrossroadsEntity>) => {
    return params.data.goid
  }, [])

  const getMappingRowId = useCallback(
    (params: GetRowIdParams<CrossroadsMapping>) => {
      return params.data._id!
    },
    [],
  )

  const onFilterSelectionSwitchToggled = useCallback(
    (checked: boolean) => {
      setFilterToSelectedMapping(checked)
    },
    [setFilterToSelectedMapping],
  )

  const onRowClicked = useCallback(
    (event: RowClickedEvent<CrossroadsEntity>) => {
      if (selectedEntity && selectedEntity._id == event.data!._id) {
        setSelectedEntity(null)
        event.node.setSelected(false)
      } else {
        setSelectedEntity(event.data!)
        event.node.setSelected(true)
      }
    },
    [selectedEntity],
  )

  const tenantGoidOptions = useMemo(() => {
    const partnerNames = new Set(
      selectedTenant?.partners?.map((p) => p.tenant_name) ?? [],
    )
    const hasPartners = partnerNames.size > 0
    const visibleTenants = hasPartners
      ? availableTenants.filter(
          (t) =>
            t.goid &&
            (t.name === selectedTenant?.name || partnerNames.has(t.name)),
        )
      : availableTenants.filter((t) => t.goid)

    const sorted = [...visibleTenants].sort((a, b) =>
      a.name.localeCompare(b.name),
    )

    return [
      { label: 'All Tenants', value: '' },
      ...sorted.map((t) => ({ label: t.name, value: t.goid! })),
    ]
  }, [availableTenants, selectedTenant])

  const actionButtons = (
    <Horizontal gap={12} alignItems='center'>
      <Select
        placeholder='Filter by Tenant'
        showSearch
        optionFilterProp='label'
        value={selectedTenantGoid ?? ''}
        onChange={(v) => setSelectedTenantGoid(v || undefined)}
        options={tenantGoidOptions}
        style={{ minWidth: 180 }}
      />
      <Texto style={{ paddingRight: '4pt', whiteSpace: 'nowrap' }}>
        Selected Mapping Only
      </Texto>
      <Switch
        defaultChecked={false}
        onChange={onFilterSelectionSwitchToggled}
      />
    </Horizontal>
  )

  const customSearchBar = (
    <Input
      placeholder='Search Entities'
      onChange={(e) => setSearchTerm(e.target.value)}
    />
  )

  return (
    <PanelGroup direction='vertical'>
      <Panel defaultSize={100} minSize={25}>
        <GraviGrid<CrossroadsEntity, Record<string, unknown>>
          key={`mrd-grid`}
          controlBarProps={{
            title: 'Master Reference Data',
            actionButtons,
            customSearchBar,
            rowCount: totalRows,
          }}
          columnDefs={colDefs}
          agPropOverrides={{
            defaultColDef: {
              resizable: true,
              filter: false,
              sortable: true,
            },
            domLayout: 'normal',
            animateRows: true,
            rowGroupPanelShow: 'never',
            getRowId: getRowId,
            pagination: true,
            paginationPageSize: 500,
            cacheBlockSize: 1000,
            maxBlocksInCache: 3,
            rowModelType: 'serverSide',
            serverSideDatasource: datasource,
            suppressMultiRangeSelection: true,
            suppressRowClickSelection: true,
            onRowClicked: onRowClicked,
          }}
        />
      </Panel>
      {selectedEntity && (
        <>
          <PanelResizeHandle
            style={{
              height: '8px',
              background: 'var(--gray-200)',
              cursor: 'row-resize',
            }}
          />
          <Panel defaultSize={25} maxSize={40}>
            <GraviGrid<CrossroadsMapping, Record<string, unknown>>
              key={'linked-mapping-grid'}
              controlBarProps={{
                title: 'Linked Mappings',
                customSearchBar: <></>,
              }}
              columnDefs={linkedMappingGridColDefs}
              agPropOverrides={{
                defaultColDef: {
                  resizable: true,
                  filter: false,
                },
                domLayout: 'normal',
                animateRows: true,
                rowGroupPanelShow: 'never',
                getRowId: getMappingRowId,
                suppressRowClickSelection: true,
                masterDetail: true,
                detailCellRendererParams: detailGridRendererParams,
                isRowMaster: () => true,
                detailRowAutoHeight: true,
              }}
              rowData={linkedEntities}
              loading={linkedEntitiesLoading}
            />
          </Panel>
        </>
      )}
    </PanelGroup>
  )
}
